import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchExamsDashboard, deleteExam } from '../../../../api/examApi';
import ExamMasterPage from '../ExamMaster/ExamMasterPage';
import Modal from 'react-modal';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ExamMasterList.css';
import CommonDataTable from '../../../../components/CustomTable/CustomTable';

Modal.setAppElement('#root');

const ExamMasterList = () => {
  const [exams, setExams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    document.title = 'Exam Master - Finhaat Gurukul';
  }, []);

  const loadExams = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        search: 'isDeleted:eq:0',
        sortBy: "examName",
        sortDirection: "asc",
        page: 0,
        size: 1000 
      };
      const data = await fetchExamsDashboard(params);
      setExams(data.content);
    } catch (error) {
      console.error('Error fetching exams:', error);
      toast.error('Oops! Finhaat hit a data snag. Try again soon!');
      setExams([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExams();
  }, [loadExams]);
  
  const handleAddExam = () => {
    setSelectedExam(null);
    setIsModalOpen(true);
  };

  const handleEditExam = (exam) => {
    setSelectedExam(exam);
    setIsModalOpen(true);
  };

  const handleDeleteExam = async (examId) => {
    if (window.confirm('Are you sure you want to delete this exam?')) {
      setIsLoading(true);
      try {
        await deleteExam(examId);
        await loadExams();
        toast.success('Exam deleted successfully!', {
          icon: '🗑️'
        });
      } catch (error) {
        console.error('Error deleting exam:', error);
        toast.error('Oops! Finhaat hit a data snag. Try again soon!', {
          icon: '❌'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExam(null);
    loadExams();
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedExam(null);
    setHighlightedRowId(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleViewCategories = (exam) => {
    setSelectedExam(exam);
    setIsCategoryModalOpen(true);
    setHighlightedRowId(exam.examId);
  };

  const filteredExams = useMemo(() => {
    return exams.filter(exam =>
      exam.examName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.examType.examTypeDesc.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [exams, searchTerm]);

  const paginatedExams = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredExams.slice(start, start + perPage);
  }, [filteredExams, currentPage, perPage]);


  const columns = [
    {
      name: 'Exam Name',
      selector: row => row.examName,
      sortable: true,
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          {row.examName}
        </div>
      ),
    },
    {
      name: 'Exam Type',
      selector: row => row.examType.examTypeDesc,
      sortable: true,
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          {row.examType.examTypeDesc}
        </div>
      ),
    },
    {
      name: 'Questions',
      selector: row => row.numberOfQuestions,
      sortable: true,
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          {row.numberOfQuestions}
        </div>
      ),
    },
    {
      name: 'Timed',
      selector: row => row.isTimed ? 'Yes' : 'No',
      sortable: true,
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          {row.isTimed ? 'Yes' : 'No'}
        </div>
      ),
    },
    {
      name: 'Duration (mins)',
      selector: row => row.durationMinutes,
      sortable: true,
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          {row.durationMinutes}
        </div>
      ),
    },
    {
      name: 'Categories',
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          <button onClick={() => handleViewCategories(row)} className="action-btn view-btn-table-modal">
            <FaEye />
          </button>
        </div>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ 
          backgroundColor: row.examId === highlightedRowId ? '#FAC7C7' : 'transparent',
          color: row.examId === highlightedRowId ? '#FF0064' : 'inherit',
          width: '100%',
          minHeight: '45px',
          padding: '8px',
          borderRadius: '5px',
          fontWeight: '550',
        }}>
          <button onClick={() => handleEditExam(row)} className="action-btn edit-btn">
            <FaEdit />
          </button>
          <button onClick={() => handleDeleteExam(row.examId)} className="action-btn delete-btn">
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const modalStyles = {
    content: {
      width: '80%',
      maxWidth: '1200px',
      height: 'auto',
      maxHeight: '90vh',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      padding: '20px',
      borderRadius: '8px',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
  };

  const categoryModalStyles = {
    content: {
      width: '300px',
      height: '100%',
      top: '0',
      right: '0',
      bottom: '0',
      left: 'auto',
      padding: '20px',
      borderRadius: '0',
      border: 'none',
      boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.1)',
      transform: 'translateX(0)',
      transition: 'transform 0.3s ease-out',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
    },
  };

  return (
    <div className="exam-master-list">
      <div className="header">
        <h2 className='header-exam-master-list'>Exam Master List</h2>
        <button onClick={handleAddExam} className="add-btn">
          <FaPlus /> Add Exam Master
        </button>
      </div>
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by Exam Name or Type..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      <CommonDataTable
        columns={columns}
        data={paginatedExams}
        totalRows={filteredExams.length}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        isLoading={isLoading}
        
        currentPage={currentPage}
        perPage={perPage}
      />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Exam Master Modal"
        className="exam-master-modal"
        overlayClassName="exam-master-modal-overlay"
        style={modalStyles}
      >
        <button onClick={handleCloseModal} className="modal-close-btn">
          <FaTimes />
        </button>
        <ExamMasterPage examToEdit={selectedExam} onClose={handleCloseModal} />
      </Modal>
      
      <Modal
        isOpen={isCategoryModalOpen}
        onRequestClose={handleCloseCategoryModal}
        contentLabel="Category Modal"
        className="category-modal-table-modal"
        overlayClassName="category-modal-overlay-table-modal"
        style={categoryModalStyles}
      >
        <button onClick={handleCloseCategoryModal} className="modal-close-btn-table-modal">
          <FaTimes />
        </button>
        <h3>{selectedExam?.examName}</h3>
        <h4>Categories and Weightage</h4>
        <ul className="category-list-table-modal">
          {selectedExam?.examCategory.map((category, index) => (
            <li key={index} className="category-item-table-modal">
              <span className="category-name-table-modal">{category.category.categoryName}</span>
              <span className="category-weightage-table-modal">{category.percentageWeightage}%</span>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default ExamMasterList;